/* DeliveryProductsTable: on a delivery, a cargo recap with several products
 * (go-live card 1248h535zfw; Chris's calls 2026-10-21). Each ship is its own
 * delivery: tick the products it carries and how much of each, and give a
 * product its own adjusted price when its quality moved it, and after loading
 * what of it went on board under this side's contract (this side's invoice
 * bills that; the other side's table has its own). Saved through
 * set-cargo-products; the rules (and the refusals' words) are the server's,
 * backend/deliveries/services/cargo_products.py. Both looks.
 */
import React, { useEffect, useRef, useState } from 'react';
import axiosInstance from '../../utils/axiosConfig';
import { PRICE_DECIMALS, QUANTITY_DECIMALS, formatFigure } from '../../utils/precision';
import '../DealProductLines.css';

const show = (value, places, maxPlaces) => (
  value == null || value === '' ? '—' : formatFigure(value, places, maxPlaces)
);

export const rowsFromServer = (cargoProducts) => (
  (Array.isArray(cargoProducts) ? cargoProducts : []).map((row) => ({
    recap_product: row.recap_product,
    label: row.product_label || 'Product',
    onRecap: row.on_recap,
    left: row.left,
    price: row.price,
    loaded: row.loaded != null ? String(row.loaded) : '',
    // Carried: a product row, or every line of a recap the ship holds whole.
    ticked: row.on_this_delivery != null || row.carried === true,
    quantity: row.on_this_delivery != null ? String(row.on_this_delivery) : '',
    adjusted: row.adjusted_price != null ? String(row.adjusted_price) : '',
  }))
);

// What the save sends: the ticked products only (the others leave this ship).
export const rowsForSave = (rows) => rows
  .filter((row) => row.ticked)
  .map((row) => ({
    recap_product: row.recap_product,
    quantity: String(row.quantity || '').replace(/[\s,]/g, ''),
    adjusted_price: String(row.adjusted || '').replace(/[\s,]/g, '') || null,
    loaded_quantity: String(row.loaded || '').replace(/[\s,]/g, '') || null,
  }));

const DeliveryProductsTable = ({ deliveryId, recap, unit = '', currency = '', onSavedFlash, onSaved }) => {
  const [rows, setRows] = useState(() => rowsFromServer(recap?.cargo_products));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');

  // Reseed when another delivery or recap, or the server's figures for it,
  // arrive. A table holding unsaved changes keeps them when the other side's
  // save re-reads the same delivery.
  const seed = JSON.stringify(recap?.cargo_products || []);
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;
  const seededFor = useRef(`${deliveryId}:${recap?.id}`);
  useEffect(() => {
    const target = `${deliveryId}:${recap?.id}`;
    if (dirtyRef.current && seededFor.current === target) return;
    seededFor.current = target;
    setRows(rowsFromServer(recap?.cargo_products));
    setDirty(false);
    setError('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryId, recap?.id, seed]);

  const change = (id, patch) => {
    setRows((prev) => prev.map((row) => (row.recap_product === id ? { ...row, ...patch } : row)));
    setDirty(true);
    setError('');
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const response = await axiosInstance.post(`/api/deliveries/${deliveryId}/set-cargo-products/`, {
        recap_id: recap.id,
        rows: rowsForSave(rows),
      });
      setRows(rowsFromServer(response.data?.rows));
      setDirty(false);
      if (onSavedFlash) onSavedFlash('Products saved.', true);
      // The delivery's totals moved with its products: the screen re-reads them.
      if (onSaved) onSaved();
    } catch (err) {
      // The server says what is wrong in words; show them as they are.
      setError(err?.response?.data?.error || 'The products could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const unitText = unit ? ` (${unit})` : '';
  const currencyText = currency ? ` (${currency})` : '';
  return (
    <div className="delivery-several-products">
      <p className="delivery-several-products__lead">
        This deal has several products. Tick the ones this delivery carries and how much of each.
        A product&apos;s price can be adjusted on its own, and after loading, say what of it went on board
        under this contract (this side&apos;s invoice bills it).
      </p>
      <div className="delivery-products-scroll">
      <table className="delivery-products-table">
        <thead>
          <tr>
            <th scope="col" aria-label="On this delivery" />
            <th scope="col">Product</th>
            <th scope="col" className="num">{`On the recap${unitText}`}</th>
            <th scope="col" className="num">Left for this delivery</th>
            <th scope="col" className="num">{`This delivery${unitText}`}</th>
            <th scope="col" className="num">{`Price${currencyText}`}</th>
            <th scope="col" className="num">Adjusted price</th>
            <th scope="col" className="num">{`Loaded on board${unitText}`}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.recap_product}>
              <td>
                <input
                  type="checkbox"
                  aria-label={`${row.label} is on this delivery`}
                  checked={row.ticked}
                  disabled={!deliveryId}
                  onChange={(e) => change(row.recap_product, {
                    ticked: e.target.checked,
                    // Ticking offers what is left; the person can change it.
                    quantity: e.target.checked && !row.quantity && row.left != null ? String(row.left) : row.quantity,
                  })}
                />
              </td>
              <td className="product">{row.label}</td>
              <td className="num">{show(row.onRecap, 0, QUANTITY_DECIMALS)}</td>
              <td className="num">{show(row.left, 0, QUANTITY_DECIMALS)}</td>
              <td className="num">
                <input
                  className="hms-input"
                  inputMode="decimal"
                  aria-label={`${row.label} quantity on this delivery`}
                  value={row.quantity}
                  disabled={!row.ticked || !deliveryId}
                  onChange={(e) => change(row.recap_product, { quantity: e.target.value })}
                />
              </td>
              <td className="num">{show(row.price, 2, PRICE_DECIMALS)}</td>
              <td className="num">
                <input
                  className="hms-input"
                  inputMode="decimal"
                  aria-label={`${row.label} adjusted price`}
                  placeholder={row.price != null ? formatFigure(row.price, 2, PRICE_DECIMALS) : ''}
                  value={row.adjusted}
                  disabled={!row.ticked || !deliveryId}
                  onChange={(e) => change(row.recap_product, { adjusted: e.target.value })}
                />
              </td>
              <td className="num">
                <input
                  className="hms-input"
                  inputMode="decimal"
                  aria-label={`${row.label} loaded on board`}
                  placeholder="after loading"
                  value={row.loaded}
                  disabled={!row.ticked || !deliveryId}
                  onChange={(e) => change(row.recap_product, { loaded: e.target.value })}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {!deliveryId && (
        <p className="delivery-several-products__lead">Save the delivery first, then tick its products.</p>
      )}
      {error && <div className="hms-deal-lines-error" role="alert">{error}</div>}
      {deliveryId && (
        <button type="button" className="hms-deal-lines__add" onClick={save} disabled={saving || !dirty}>
          {saving ? 'Saving…' : 'Save products'}
        </button>
      )}
    </div>
  );
};

export default DeliveryProductsTable;
